import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, LogOut, User, FileText, Briefcase, BarChart3, CreditCard, Shield, TrendingUp, DollarSign, Bell, Receipt, Clock } from 'lucide-react';
import LoginModal from './LoginModal';
import SignupModal from './SignupModal';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [quota, setQuota] = useState(null);
  const [newQuotesCount, setNewQuotesCount] = useState(0);
  const [pendingInvoiceCount, setPendingInvoiceCount] = useState(0);
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();

  // Fetch quota for authenticated users
  useEffect(() => {
    const fetchQuota = async () => {
      if (user?.id && user?.userType === 'homeowner') {
        try {
          const response = await axios.get(`${API_URL}/api/users/${user.id}/quota`);
          setQuota(response.data.quota);
        } catch (error) {
          console.error('Error fetching quota:', error);
        }
      }
    };

    if (isAuthenticated) {
      fetchQuota();
      // Refresh quota every 30 seconds
      const interval = setInterval(fetchQuota, 30000);
      return () => clearInterval(interval);
    }
  }, [user, isAuthenticated]);

  // Fetch new quote requests for professionals
  useEffect(() => {
    const fetchNewQuotes = async () => {
      if (user?.email && user?.userType === 'professional') {
        try {
          // Get provider ID
          const providersRes = await axios.get(`${API_URL}/api/providers`);
          let provider = providersRes.data.find(p => p.email === user.email);
          if (!provider) {
            provider = providersRes.data.find(p => p.name === user.name);
          }
          
          if (provider) {
            // Get pending quotes for this provider
            const quotesRes = await axios.get(`${API_URL}/api/quotes?providerId=${provider.id}`);
            const pendingQuotes = quotesRes.data.filter(q => q.status === 'pending');
            setNewQuotesCount(pendingQuotes.length);
          }
        } catch (error) {
          console.error('Error fetching new quotes:', error);
        }
      }
    };

    if (isAuthenticated) {
      fetchNewQuotes();
      // Refresh every 30 seconds
      const interval = setInterval(fetchNewQuotes, 30000);
      return () => clearInterval(interval);
    }
  }, [user, isAuthenticated]);

  // Fetch pending invoice count for professionals
  useEffect(() => {
    const fetchPendingInvoice = async () => {
      if (user?.email && user?.userType === 'professional') {
        try {
          const response = await axios.get(`${API_URL}/api/leads/pending-invoice`, {
            params: { email: user.email }
          });
          setPendingInvoiceCount(response.data.leadCount || 0);
        } catch (error) {
          console.error('Error fetching pending invoice:', error);
        }
      }
    };

    if (isAuthenticated) {
      fetchPendingInvoice();
      // Refresh every 30 seconds
      const interval = setInterval(fetchPendingInvoice, 30000);
      return () => clearInterval(interval);
    }
  }, [user, isAuthenticated]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_leadpay-system/artifacts/zoevafok_Profind-logo.png" 
                alt="Profind.ca" 
                style={{ height: '160px' }}
                className="w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/providers" className="text-gray-700 hover:text-orange-600 font-medium transition-colors">
                Find Pros
              </Link>
              {isAuthenticated && user?.userType === 'professional' ? (
                <Link to="/pro-quotes" className="text-gray-700 hover:text-orange-600 font-medium transition-colors relative">
                  Get Leads
                  {newQuotesCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {newQuotesCount}
                    </span>
                  )}
                </Link>
              ) : (
                <Link to="/pricing" className="text-gray-700 hover:text-orange-600 font-medium transition-colors">
                  Pricing
                </Link>
              )}
              
              {isAuthenticated ? (
                <>
                  {/* Lead Quota Indicator for Free Tier Professionals */}
                  {user?.userType === 'professional' && user?.subscriptionTier === 'free' && quota && (
                    <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full text-sm">
                      <FileText className="h-4 w-4 text-gray-600" />
                      <span className="text-gray-700 font-medium">
                        {quota.limit === null ? 'Unlimited Leads' : `${quota.remaining}/${quota.limit} Leads`}
                      </span>
                      {quota.limit && quota.remaining === 0 && (
                        <span className="text-xs text-red-600 font-semibold ml-1">LIMIT REACHED</span>
                      )}
                      {quota.limit && quota.remaining <= 1 && quota.remaining > 0 && (
                        <span className="text-xs text-orange-600 font-semibold ml-1">LOW</span>
                      )}
                    </div>
                  )}
                  
                  {/* Pending Invoice Checkout Badge for Professionals */}
                  {user?.userType === 'professional' && pendingInvoiceCount > 0 && (
                    <button
                      onClick={() => navigate('/pending-invoice')}
                      className="hidden md:flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors relative"
                    >
                      <CreditCard className="h-4 w-4" />
                      <span>Checkout</span>
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                        {pendingInvoiceCount}
                      </span>
                    </button>
                  )}
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-orange-600" />
                        </div>
                        <span className="text-gray-700 font-medium">{user?.name}</span>
                      </Button>
                    </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>My Account</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')}>
                      <User className="mr-2 h-4 w-4" />
                      Profile
                    </DropdownMenuItem>
                    {user?.userType === 'homeowner' && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/request-quote')}>
                          <FileText className="mr-2 h-4 w-4" />
                          Request a Quote
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/my-quotes')}>
                          My Quote Requests
                        </DropdownMenuItem>
                      </>
                    )}
                    {user?.userType === 'professional' && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/become-a-pro')}>
                          <Briefcase className="mr-2 h-4 w-4" />
                          Create Business Profile
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/pro-quotes')} className="relative">
                          <div className="flex items-center justify-between w-full">
                            <span>Quote Requests</span>
                            {newQuotesCount > 0 && (
                              <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                                {newQuotesCount}
                              </span>
                            )}
                          </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/analytics')}>
                          <BarChart3 className="mr-2 h-4 w-4" />
                          Analytics
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/subscription')}>
                          <CreditCard className="mr-2 h-4 w-4" />
                          Subscription
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/packages')}>
                          <DollarSign className="mr-2 h-4 w-4" />
                          My Packages
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/edit-business-profile')}>
                          <Briefcase className="mr-2 h-4 w-4" />
                          Edit Business Profile
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/pending-invoice')} className="relative">
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center">
                              <Clock className="mr-2 h-4 w-4" />
                              <span>Pending Invoice</span>
                            </div>
                            {pendingInvoiceCount > 0 && (
                              <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                                {pendingInvoiceCount}
                              </span>
                            )}
                          </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/invoices')}>
                          <Receipt className="mr-2 h-4 w-4" />
                          Invoices & Billing
                        </DropdownMenuItem>
                      </>
                    )}
                    {user?.userType === 'admin' && (
                      <DropdownMenuItem onClick={() => navigate('/admin')}>
                        <Shield className="mr-2 h-4 w-4" />
                        Admin Dashboard
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate('/settings')}>
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => setLoginOpen(true)}
                    className="text-gray-700 hover:text-orange-600 font-medium"
                  >
                    Sign In
                  </Button>
                  <Button
                    onClick={() => setSignupOpen(true)}
                    className="bg-orange-600 hover:bg-orange-700 text-white font-medium"
                  >
                    Join Now
                  </Button>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-4 space-y-3">
              <Link
                to="/providers"
                className="block text-gray-700 hover:text-orange-600 font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Find Pros
              </Link>
              {isAuthenticated && user?.userType === 'professional' ? (
                <>
                  <Link
                    to="/pro-quotes"
                    className="block text-gray-700 hover:text-orange-600 font-medium py-2 relative"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className="flex items-center justify-between">
                      <span>Get Leads</span>
                      {newQuotesCount > 0 && (
                        <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                          {newQuotesCount}
                        </span>
                      )}
                    </div>
                  </Link>
                  
                  {/* Mobile Checkout Button */}
                  {pendingInvoiceCount > 0 && (
                    <button
                      onClick={() => {
                        navigate('/pending-invoice');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center justify-between bg-orange-600 hover:bg-orange-700 text-white rounded-lg px-4 py-3 font-medium transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        <span>Checkout Now</span>
                      </div>
                      <span className="bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                        {pendingInvoiceCount}
                      </span>
                    </button>
                  )}
                </>
              ) : (
                <Link
                  to="/pricing"
                  className="block text-gray-700 hover:text-orange-600 font-medium py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Pricing
                </Link>
              )}
              
              {isAuthenticated ? (
                <>
                  <div className="py-2 border-t border-gray-200">
                    <p className="text-sm text-gray-500 mb-2">Signed in as</p>
                    <p className="font-semibold text-gray-900">{user?.name}</p>
                    <p className="text-xs text-gray-500">{user?.userType === 'homeowner' ? 'Homeowner' : 'Professional'}</p>
                  </div>
                  <Link
                    to="/profile"
                    className="block text-gray-700 hover:text-orange-600 font-medium py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Profile
                  </Link>
                  {user?.userType === 'homeowner' && (
                    <>
                      <Link
                        to="/request-quote"
                        className="block text-gray-700 hover:text-orange-600 font-medium py-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Request a Quote
                      </Link>
                      <Link
                        to="/my-quotes"
                        className="block text-gray-700 hover:text-orange-600 font-medium py-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        My Quote Requests
                      </Link>
                    </>
                  )}
                  {user?.userType === 'professional' && (
                    <>
                      <Link
                        to="/pro-quotes"
                        className="block text-gray-700 hover:text-orange-600 font-medium py-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <div className="flex items-center justify-between">
                          <span>Quote Requests</span>
                          {newQuotesCount > 0 && (
                            <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                              {newQuotesCount}
                            </span>
                          )}
                        </div>
                      </Link>
                      <Link
                        to="/analytics"
                        className="block text-gray-700 hover:text-orange-600 font-medium py-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Analytics
                      </Link>
                      <Link
                        to="/subscription"
                        className="block text-gray-700 hover:text-orange-600 font-medium py-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Subscription
                      </Link>
                      <Link
                        to="/pending-invoice"
                        className="block text-gray-700 hover:text-orange-600 font-medium py-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <div className="flex items-center justify-between">
                          <span>Pending Invoice</span>
                          {pendingInvoiceCount > 0 && (
                            <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                              {pendingInvoiceCount}
                            </span>
                          )}
                        </div>
                      </Link>
                      <Link
                        to="/invoices"
                        className="block text-gray-700 hover:text-orange-600 font-medium py-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Invoices & Billing
                      </Link>
                    </>
                  )}
                  <Link
                    to="/settings"
                    className="block text-gray-700 hover:text-orange-600 font-medium py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Settings
                  </Link>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={handleLogout}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setLoginOpen(true);
                      setMobileMenuOpen(false);
                    }}
                  >
                    Sign In
                  </Button>
                  <Button
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                    onClick={() => {
                      setSignupOpen(true);
                      setMobileMenuOpen(false);
                    }}
                  >
                    Join Now
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} onSignupClick={() => { setLoginOpen(false); setSignupOpen(true); }} />
      <SignupModal open={signupOpen} onClose={() => setSignupOpen(false)} onLoginClick={() => { setSignupOpen(false); setLoginOpen(true); }} />
    </>
  );
};

export default Navbar;